import React, { useState, useRef, useEffect, useCallback, useMemo, useLayoutEffect } from "react";
import axiosInstance from "../apis/gvmdesk";
import useAxiosFunction from "../hooks/useAxiosFunction";
import { useHttp } from "../hooks/useHttp";
import { useChats } from "../hooks/useChats";
import { useMessages } from "../hooks/useMessages";
import { useDropzone } from "react-dropzone";
import api from "../services/api";
import openToast from "../utils/sweetAlert";
import moment from "moment";
import "moment/locale/pt-br";
import Message from "../svgs/Message";
import Spinner from "../components/Spinner";
import Messages from "../components/Messages";
import SmallButton from "../components/SmallButton";
import { useAuth } from "../providers/auth";
import {  useForm } from "react-hook-form";
import MessageInfo from "../components/MessageInfo";
import ChatModals from "../components/ChatModals";
import Popup from "../components/Popup";
import LoadingCenter from "../components/LoadingCenter";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useMutation } from "@tanstack/react-query";
import { useTheme } from "../hooks/useTheme";
import ChatHeader from "../components/ChatHeader";
import ChatComposer from "../components/ChatComposer";
import ChatGroupHeader from "../components/ChatGroupHeader";
import BulkComposer from "../components/BulkComposer";
import ChatList from "../components/ChatList";
import ChatSidebar from "../components/ChatSidebar";
import { formatAdminTransferLabel } from "../utils/porte";

const prospectSchema = yup.object({
  prospect: yup.string().required("É necessário selecionar uma opção acima!").nullable()
}).required();

// Filtro de vendedor (apenas para master)
const sellerSchema = yup.object({
  seller: yup.string().required("É necessário selecionar uma opção acima!").nullable()
}).required();

const ALL_CHATS_GROUPS = [1, 2, 7, 8, 9, 10];
const CHAT_PAGE_SIZE = 30;
const CHAT_SYNC_INTERVAL_MS = 10_000;
const normalizeSearchText = text => String(text || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "");
const normalizeWhatsappMediaMimeType = mimeType => {
  const normalized = String(mimeType || "").toLowerCase();

  return normalized === "audio/mp3" || normalized === "audio/x-mp3" ? "audio/mpeg" : normalized;
};
const getWhatsappMediaFormat = mimeType => {
  const baseMimeType = normalizeWhatsappMediaMimeType(mimeType).split(";")[0];
  const audioFormats = {
    "audio/aac": ".aac",
    "audio/amr": ".amr",
    "audio/mpeg": ".mp3",
    "audio/mp4": ".m4a",
    "audio/ogg": ".ogg",
  };

  if (audioFormats[baseMimeType]) {
    return audioFormats[baseMimeType];
  }

  const subtype = baseMimeType.split("/")[1] || "";
  return subtype ? "." + subtype : "";
};

export function Chat() {
  const { theme } = useTheme();

  const blockLongPooling = useRef(false);

  const currentChat = useRef(null);

  const { user } = useAuth();
  const canViewAllChats = useMemo(() => ALL_CHATS_GROUPS.includes(Number(user?.group_id)), [user?.group_id]);
  const isChatMasterRoute = typeof window !== 'undefined' && window.location.pathname.includes('chat-master');
  const [chatScope, setChatScope] = useState(() => isChatMasterRoute ? "all" : "mine");
  const isMasterMode = useMemo(() => canViewAllChats && chatScope === "all", [canViewAllChats, chatScope]);
  const adminId = useRef(0);
  const chatScopeRef = useRef(chatScope);
  const chatListRequest = useRef(0);
  const chatListScrollRef = useRef(null);
  const chatListScrollTop = useRef(0);
  const chatPage = useRef(1);
  const [masterGate, setMasterGate] = useState(false);

  useEffect(() => {
    chatScopeRef.current = chatScope;
  }, [chatScope]);

  const { register, handleSubmit, watch, formState: { errors } } = useForm({
    defaultValues: {
      chats: []
    }
  });


  const { register: registerProspect, handleSubmit: handleSubmitProspect, reset: resetProspect, formState: { errors: prospectErrors } } = useForm({
    resolver: yupResolver(prospectSchema)
  });

  // Formulário do filtro por vendedor (master)
  const { register: registerSeller, handleSubmit: handleSubmitSeller, formState: { errors: sellerErrors } } = useForm({
    resolver: yupResolver(sellerSchema)
  });
  // Headers padronizados p/ API
  const tokenHeader = useMemo(() => ({ headers: { Authorization: "Bearer " + user?.token } }), [user?.token]);
  // Utilitários de chat (hooks)
  const { clearLocalCache, fetchByTags } = useChats(user);
  // Funções auxiliares de mensagens
  const { fetchChatInfo } = useMessages(user);

  const textArea = useRef(null);

  const [notAllowed, setNotAllowed] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [chatToBottom, setChatToBottom] = useState(0);
  const [buttonPopupError, setButtonPopupError] = useState(false);
  const [buttonPopupSuccess, setButtonPopupSuccess] = useState(false);
  const [currentChatId, setCurrentChatId] = useState("");
  const currentChatIdRef = useRef("");
  const [available, setAvailable] = useState(false);
  const [isChatChosen, setIsChatChosen] = useState(false);
  const [isChatSelected, setIsChatSelected] = useState(false);
  const [isNewChatChosen, setIsNewChatChosen] = useState(false);
  const [forwardMessages, setforwardMessages] = useState("");
  const [isNewChat, setIsNewChat] = useState(false);
  const [isModalOptionsOpen, setIsModalOptionsOpen] = useState(false);

  // Estado do filtro de tags (evita erros de escopo temporal)
  const [selectedTags, setSelectedTags] = useState([]);

  const openNewContactPopup = useCallback((e) => {
    e.preventDefault();
    setButtonPopupNewContact(true);
  }, []);

  const toggleOptionsMenu = useCallback(() => {
    setIsModalOptionsOpen(prev => !prev);
    blockLongPooling.current = false;
  }, []);

  const openFilterByTags = useCallback(() => {
    setButtonPopupChatTags(true);
    blockLongPooling.current = true;
  }, []);

  const clearTagsFilter = useCallback(() => {
    limparTags();
  }, []);

  const [isEmojiModalOpen, setIsEmojiModalOpen] = useState(false);
  const [buttonPopupObs, setButtonPopupObs] = useState(false);
  const [buttonPopupForward, setButtonPopupForward] = useState(false);
  const [info, setInfo] = useState();
  const [infoLoading, setInfoLoading] = useState(false);
  const [infoLoadingTransfer, setInfoLoadingTransfer] = useState(false);
  const [search, setSearch] = useState("");
  const [searchMessages, setSearchMessages] = useState([]);
  const [listPhones, setListPhones] = useState([]);
  const [socialMedias, setSocialMedias] = useState([]);
  const [idCustomer, setIdCustomer] = useState("");
  const [infoLoadingLigar, setInfoLoadingLigar] = useState(false);


  // LIGAR
  async function pegaCliente(id) {
    const options = { headers: { "Authorization": "Bearer " + user.token } };
    try {
      setInfoLoadingLigar(true);
      const response = await api.get("/users/list/phone/" + id, options);
      setListPhones(response.data);

    } catch (error) {
      
    } finally {
      setInfoLoadingLigar(false);
    }
  }

  const [currentReplyMessage, setCurrentReplyMessage] = useState(null);

  const replyMessage = (e, message) => {
    e.preventDefault();
    setIsEmojiModalOpen(true);

    setCurrentReplyMessage({
      id: message.id,
      body: message.body,
      author: message.name_user,
      sender: message.sender_id
    });
  }

  const closeReplyMessage = () => {
    setIsEmojiModalOpen(false);
    setCurrentReplyMessage(null);
  }

  useEffect(() => {
    currentChatIdRef.current = String(currentChatId || "");
  }, [currentChatId]);

  /** Módulo: Chats — Estado e carregamento */

  //CHATS
  const [chats, setChats] = useState([]);
  const [list, setList] = useState([]);
  const [listUsers, setListUsers] = useState([]);
  const [users, setUsers] = useState([]);
  const [isChatsFetching, setIsChatsFetching] = useState(true);
  const [hasMoreChats, setHasMoreChats] = useState(true);
  const [isLoadingMoreChats, setIsLoadingMoreChats] = useState(false);
  // Evite usar globais para rastrear o chat atual

  /** Atualiza a lista de chats conforme filtros (tags) */
  const normalizeChatItem = useCallback(c => ({
    ...c,
    message: c.message ?? c.last_message ?? c.lastMessage ?? c.last_message_preview ?? c.lastMessagePreview ?? "",
    notReadMessages: c.notReadMessages ?? c.unread ?? c.unread_count ?? c.unreadCount ?? 0,
    read: c.read ?? ((c.notReadMessages ?? c.unread ?? c.unread_count ?? c.unreadCount ?? 0) <= 0),
    updateAt: c.updateAt ?? c.updatedAt ?? c.updated_at ?? c.last_message_at ?? c.lastMessageAt ?? c.repliedAt ?? c.replied_at ?? c.createdAt,
  }), []);

  const normalizeList = useCallback((arr = []) => arr.map(normalizeChatItem), [normalizeChatItem]);

  const getChatKey = useCallback(chat => String(chat?.chat_id ?? chat?.id ?? chat?.chatId ?? ""), []);

  const mergeChatLists = useCallback((current = [], incoming = []) => {
    const byId = new Map();

    current.forEach(chat => byId.set(getChatKey(chat), chat));
    incoming.forEach(chat => byId.set(getChatKey(chat), chat));

    return Array.from(byId.values());
  }, [getChatKey]);

  const mergeIncomingChatsByServerOrder = useCallback((current = [], incoming = []) => {
    const currentById = new Map();
    const incomingKeys = new Set();

    current.forEach(chat => {
      const key = getChatKey(chat);
      if (key) currentById.set(key, chat);
    });

    const orderedIncoming = incoming.map(chat => {
      const key = getChatKey(chat);
      if (key) incomingKeys.add(key);

      const currentChat = currentById.get(key);
      if (!currentChat) return chat;

      return {
        ...currentChat,
        ...chat,
        checked: currentChat.checked ?? chat.checked,
      };
    });

    const remainingCurrent = current.filter(chat => !incomingKeys.has(getChatKey(chat)));

    return [...orderedIncoming, ...remainingCurrent];
  }, [getChatKey]);

  const filterListBySearch = useCallback((items = [], searchValue = search) => {
    if (!searchValue) return items;

    return items.filter(item =>
      normalizeSearchText(item.name).toLowerCase().includes(searchValue)
    );
  }, [search]);

  const applyChatList = useCallback((items = []) => {
    setChats(items);
    setList(filterListBySearch(items));
  }, [filterListBySearch]);

  useLayoutEffect(() => {
    if (isNewChat) return;

    const scrollElement = chatListScrollRef.current;
    if (!scrollElement) return;

    const nextTop = Math.min(
      chatListScrollTop.current,
      Math.max(0, scrollElement.scrollHeight - scrollElement.clientHeight)
    );

    if (Math.abs(scrollElement.scrollTop - nextTop) > 2) {
      scrollElement.scrollTop = nextTop;
    }
  }, [list, isChatsFetching, isNewChat]);

  const updateChatPreview = useCallback((chatId, preview, extra = {}) => {
    const now = new Date().toISOString();
    const updateItem = item => {
      if (getChatKey(item) !== String(chatId)) return item;

      return {
        ...item,
        message: preview,
        read: true,
        notReadMessages: 0,
        updateAt: now,
        ...extra,
      };
    };

    setChats(prev => prev.map(updateItem));
    setList(prev => prev.map(updateItem));
  }, [getChatKey]);

  const getOutgoingPreview = useCallback((type, body = "") => {
    if (type === "text") {
      const normalizedBody = String(body || "").trim();
      return normalizedBody.length > 40 ? "*Você:* " + normalizedBody.substr(0, 40) : "*Você:* " + normalizedBody;
    }

    if (type === "image") return "*Você:* Nova imagem";
    if (type === "video") return "*Você:* Novo vídeo";
    if (type === "audio" || type === "voice") return "*Você:* Novo áudio";
    if (type === "document" || type === "file") return "*Você:* Novo documento";

    return "*Você:* Mensagem enviada";
  }, []);

  const fetchChatsData = useCallback(async (page = 1, limit = CHAT_PAGE_SIZE, tags = selectedTags) => {
    if (Array.isArray(tags) && tags.length > 0) {
      return await fetchByTags(tags, {
        scope: isMasterMode ? "all" : "mine",
        adminId: adminId.current,
        page,
        limit,
      });
    }
    if (isMasterMode) {
      const params = new URLSearchParams({
        admin_id: adminId.current,
        page,
        limit,
      });
      const res = await api.get(`/chats/master?${params.toString()}`, tokenHeader);
      return res.data;
    }
    const res = await api.get(`/chats/admin/${page}?limit=${limit}`, tokenHeader);
    return res.data;
  }, [selectedTags, isMasterMode, adminId.current, tokenHeader, fetchByTags]);

  const updateAllChats = async () => {
    if (!search && (!Array.isArray(selectedTags) || selectedTags.length === 0)) {
      blockLongPooling.current = false;
    }
    const requestScope = chatScopeRef.current;
    const requestId = ++chatListRequest.current;
    const visibleLimit = Math.max(CHAT_PAGE_SIZE, chatPage.current * CHAT_PAGE_SIZE);

    try {
      const data = await fetchChatsData(1, visibleLimit);
      if (requestId !== chatListRequest.current || requestScope !== chatScopeRef.current) return;

      const normalized = normalizeList(data);
      applyChatList(normalized);
      setHasMoreChats(normalized.length >= visibleLimit);
    } catch (_) {}
  }

  // Atualiza a janela visível preservando seleção local, mas respeitando a ordem da API.
  const refreshChatsMeta = async () => {
    const requestScope = chatScopeRef.current;
    const requestId = chatListRequest.current;
    const visibleLimit = Math.max(CHAT_PAGE_SIZE, chatPage.current * CHAT_PAGE_SIZE);

    try {
      const src = await fetchChatsData(1, visibleLimit);
      if (requestId !== chatListRequest.current || requestScope !== chatScopeRef.current) return;

      const normalized = normalizeList(src || []);
      setHasMoreChats(normalized.length >= visibleLimit);
      setChats(prev => {
        const merged = mergeIncomingChatsByServerOrder(prev, normalized);
        setList(filterListBySearch(merged));
        return merged;
      });
    } catch (_) {}
  }
  /** Carrega/atualiza mensagens do chat e marca como lidas */
  const updateMessagesById = async (chatId, { force = false } = {}) => {
    const normalizedChatId = String(chatId || "");
    if (!normalizedChatId) return;

    const isCurrentChatRequest = () => currentChatIdRef.current === normalizedChatId;
    let nextMessages;

    if (!force && localStorage.getItem(normalizedChatId)) {
      try {
        nextMessages = JSON.parse(localStorage.getItem(normalizedChatId));
      } catch (_) {
        localStorage.removeItem(normalizedChatId);
      }
    }

    if (!nextMessages) {
      const res = await api.get(`/messages/chats/${normalizedChatId}`, tokenHeader);
      nextMessages = res.data;
      localStorage.setItem(normalizedChatId, JSON.stringify(nextMessages));
    }

    if (!isCurrentChatRequest()) return;
    setMessages(nextMessages);

    // setando chat como lido apenas se a conversa ainda estiver aberta
    await api.post(`/chats/readat/${normalizedChatId}`, { chat_id: normalizedChatId }, tokenHeader);

    if (!isCurrentChatRequest()) return;
    setBarra(0);
    // Sempre rola para o final ao atualizar mensagens
    setChatToBottom(prev => prev + 1);
  }
  const loadMoreChats = async () => {
    if (isNewChat || isChatsFetching || isLoadingMoreChats || !hasMoreChats) return;

    const nextPage = chatPage.current + 1;
    const requestScope = chatScopeRef.current;
    const requestId = ++chatListRequest.current;
    const isLatestRequest = () => requestId === chatListRequest.current && requestScope === chatScopeRef.current;

    setIsLoadingMoreChats(true);

    try {
      const data = await fetchChatsData(nextPage);
      if (!isLatestRequest()) return;

      const normalized = normalizeList(data);
      setHasMoreChats(normalized.length >= CHAT_PAGE_SIZE);

      if (normalized.length > 0) {
        chatPage.current = nextPage;
        setChats(prev => {
          const merged = mergeChatLists(prev, normalized);
          setList(filterListBySearch(merged));
          return merged;
        });
      }
    } catch (_) {
    } finally {
      if (isLatestRequest()) setIsLoadingMoreChats(false);
    }
  };

  /** Evento de scroll da lista de chats — desbloqueia loop e carrega próxima página */
  const handleScroll = async (event) => {
    if (!search && (!Array.isArray(selectedTags) || selectedTags.length === 0)) {
      blockLongPooling.current = false;
    }
    const target = event?.currentTarget;

    if (!target) return;
    chatListScrollTop.current = target.scrollTop;

    const distanceFromBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
    if (distanceFromBottom < 160) {
      loadMoreChats();
    }
  }
  /** Assume uma conversa nova para o usuário atual */
  const assumirConversa = async () => {

    setIsMessagesFetching(true);
    setIsNewChatChosen(false);
    setIsChatChosen(true);
    setCurrentChatId(currentChatId);
    setSearchMessages([]);

    try {

      const response = await api.post(`/chats/assumir/novo/${currentChatId}`, {}, { headers: { "Authorization": "Bearer " + user.token } });

      if (response.data.error) {
        setNotAllowed(response.data.message);
      } else {
        const chats = await api.get(`/messages/chats/${currentChatId}`, { headers: { "Authorization": "Bearer " + user.token } });
        setMessages(chats.data);
      }

    } catch (error) {
      
    } finally {
      setIsMessagesFetching(false);
    }
  }

  const [chatInfo, setChatInfo] = useState([]);
  const [disableUnder24hMessage, setDisableUnder24hMessage] = useState(false);
  const hasLoadedChatInfo = Boolean(chatInfo?.id || chatInfo?.chat_id);
  const isWhatsappSessionClosed = disableUnder24hMessage || chatInfo?.sessao === false || (hasLoadedChatInfo && !chatInfo?.repliedAt);
  

  /**
   * Módulo: Chats — Inicialização
   * Limpa caches locais, carrega chats e inicia long polling.
   */
  const fetchAllChats = async ({ tags = selectedTags } = {}) => {
    blockLongPooling.current = false;
    const requestScope = chatScopeRef.current;
    const requestId = ++chatListRequest.current;
    const isLatestRequest = () => requestId === chatListRequest.current && requestScope === chatScopeRef.current;

    chatPage.current = 1;
    setHasMoreChats(true);
    setIsChatsFetching(true);
    setChats([]);
    setList([]);

    try {
      const data = await fetchChatsData(1, CHAT_PAGE_SIZE, tags);
      if (!isLatestRequest()) return;

      const normalized = normalizeList(data);
      applyChatList(normalized);
      setHasMoreChats(normalized.length >= CHAT_PAGE_SIZE);
    } catch (error) {
    } finally {
      if (requestScope === chatScopeRef.current) setIsChatsFetching(false);
    }
  }

  //CHATS
  useEffect(() => {
    allStorage();
    setMasterGate(false);
    fetchAllChats();
    setIsNewChat(false);
  }, [isMasterMode]);

  /**
   * Módulo: Mensagens — Abertura e leitura
   * Abre uma conversa, carrega mensagens, marca como lida e atualiza restrições (24h).
   */
  const [isMessagesFetching, setIsMessagesFetching] = useState(false);
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState("");
  const [isSendingMessage, setIsSendingMessage] = useState(false);

// Mensagens — abrir e ler
const fetchMessagesById = async (chatId, userId, admin_id, chat) => {

  currentChat.current = chat;
  localStorage.setItem("open_chat", chatId);
  setInfoLoading(true);
  setIsMessagesFetching(true);

  if (admin_id == 0) {
    setIsNewChatChosen(true);
    setIsChatChosen(true);
  } else if (chat.admin_id == user.id) {
    setIsChatChosen(true);
    setIsNewChatChosen(false);
    setNotAllowed("");
  } else {
    setIsChatChosen(true);
    setIsNewChatChosen(false);
  }

  currentChatIdRef.current = String(chatId || "");
  setCurrentChatId(chatId);
  
  setSearchMessages([]);

  try {
    let chatData;

    const [response, response_presets, user_do_chat] = await Promise.all([
      fetchChatInfo(chatId),
      api.get(`/presets/like/g800d5m`, tokenHeader),
      api.get(`/users/${userId}`, tokenHeader),
      updateMessagesById(chatId, { force: true }),
    ]);

    chatData = response;
    setChatInfo(chatData);

    const repliedAt = chatData?.repliedAt;

    if (!repliedAt || !moment(repliedAt).isValid()) {
      setDisableUnder24hMessage(true);
    } else {
      const diffHours = moment().diff(moment(repliedAt), 'hours');
      setDisableUnder24hMessage(diffHours >= 24);
    }

    localStorage.setItem("presets", JSON.stringify(response_presets.data));
    setInfo(user_do_chat.data);
    // Leitura e barra já são tratadas por updateMessagesById
  } catch (error) {
    
  } finally {
    setInfoLoading(false);
    setIsMessagesFetching(false);
  }

  refreshChatsMeta();
  fetchFavoriteMessages(chatId);
};


  const [media, setMedia] = useState("");

  // Sincronização periódica: atualiza mensagens e metadados da lista.
  useEffect(() => {
    const interval = setInterval(() => {
      // Evita sincronizar enquanto a escolha inicial do master não for feita
      if (isMasterMode && masterGate) return;
      const openId = currentChatIdRef.current;
      if (openId && isChatChosen && !isNewChatChosen) updateMessagesById(openId, { force: true });
      if (!isNewChat) {
        if (!blockLongPooling.current) {
          updateAllChats();
        } else {
          refreshChatsMeta();
        }
      }
    }, CHAT_SYNC_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [currentChatId, user?.token, isNewChat, search, selectedTags, isMasterMode, masterGate, isChatChosen, isNewChatChosen]);

  // lupa dos chats
  const handleSearchByName = (searchText) => {
    if (searchText !== "") {
      blockLongPooling.current = true;
    } else {
      blockLongPooling.current = false;
    }

    const formattedTextSearch = normalizeSearchText(searchText).toLowerCase();

    setSearch(formattedTextSearch);

    const filteredData = chats.filter(item =>
      normalizeSearchText(item.name).toLowerCase().includes(formattedTextSearch)
    );
    setList(filteredData);
  }

  // lupa dos users
  const handleSearchUsersByName = (searchText) => {
    const formattedTextSearch = normalizeSearchText(searchText).toLowerCase();

    setSearch(formattedTextSearch);

    setListUsers(users.filter(item => {
      return normalizeSearchText(item.name).toLowerCase().includes(formattedTextSearch);
    }));
  }

  const onEmojiClick = (event, emojiObject) => {
    setMessage(message => message + emojiObject.emoji);
  };

  /**
   * Módulo: Arquivos e Colagem (individual)
   * Seleção de arquivos e colagem de imagens para envio no chat atual.
   */
  const inputFile = useRef(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedFilesSlider, setSelectedFilesSlider] = useState([]);
  const [isModalSelectedFiles, setIsModalSelectedFiles] = useState(false);
  // Imagens selecionadas (upload)
  const deleteSelectedImages = () => {
    setSelectedFiles([]);
    setSelectedFilesSlider([]);
    setIsModalSelectedFiles(false);
    setIsEmojiModalOpen(false);
  };
  // Arquivos
  const handleFiles = (e) => {
    const items = Array.from(e.target?.files || []);

    // Clona arrays para evitar mutação direta do estado
    const files = [...selectedFiles];
    const filesSlider = [...selectedFilesSlider];

    for (let i = 0; i < items.length; i++) {
      const f = items[i];
      f.path = URL.createObjectURL(f);
      filesSlider.push(f);
    }
    items.forEach(item => {
      const reader = new FileReader();
      reader.readAsDataURL(item);
      reader.onloadend = function () {
        files.push(reader.result);
        // Atualiza conforme os arquivos são lidos
        setSelectedFiles([...files]);
      }
    });

    setSelectedFilesSlider(filesSlider);
    setIsModalSelectedFiles(true);
    setIsEmojiModalOpen(true);
  }

  // Imagens coladas (estado)
  const [pasteImages, setpasteImages] = useState([]);
  const [pasteImagesSlider, setpasteImagesSlider] = useState([]);
  const [isModalpasteImages, setIsModalpasteImages] = useState(false);

  // Remover imagens coladas
  const deletePasteImages = () => {
    setpasteImages([]);
    setpasteImagesSlider([]);
    setIsModalpasteImages(false);
    setIsEmojiModalOpen(false);
  };

  const handlePasteText = (event) => {

  }

  // Tratar colagem de arquivos/imagens
  const handlePasteArchives = (e) => {
    const items = Array.from(e.clipboardData?.items || []).filter(i => i.kind === "file");
    const newImages = [];
    const newSlider = [];

    items.forEach(item => {
      const file = item.getAsFile();
      if (!file) return;
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = function () {
        newImages.push(reader.result);
        // Adiciona conforme forem carregadas
        setpasteImages(prev => [...prev, ...newImages]);
      }
      newSlider.push(URL.createObjectURL(file));
    });
    if (newSlider.length) setpasteImagesSlider(prev => [...prev, ...newSlider]);
    setIsModalpasteImages(true);
    setIsEmojiModalOpen(true);
  }

  /**
   * Módulo: Novo Chat
   * Alterna listagem de contatos e carrega contatos disponíveis.
   */
  const handleNewChat = async () => {
    blockLongPooling.current = true;
    setIsNewChat(!isNewChat);
    setIsChatsFetching(true);
    setChats([]);
    setList([]);

    try {
      const users = await api.get("/users/admin/all", { headers: { "Authorization": "Bearer " + user.token } });
      setAvailable(users.data.available);
      setUsers(users.data.contacts);
      setListUsers(users.data.contacts);
    } catch (e) {
      
    } finally {
      setIsChatsFetching(false);
    }
  }

  const loadVacationContacts = async () => {
    try {
      const users = await api.get("/users/admin/all/vacation", { headers: { "Authorization": "Bearer " + user.token } });
      setAvailable(users.data.available);
      setUsers(users.data.contacts);
      setListUsers(users.data.contacts);
    } catch (e) {
      
    } finally {
      setIsChatsFetching(false);
    }
  }

  // traz os chats normais de volta, saindo da busca por nova conversa
  const handleChatBack = () => {
    fetchAllChats();
    setUsers([]);
    setListUsers([]);
    setIsNewChat(!isNewChat);
  }

  const [isMessageSearch, setIsMessageSearch] = useState(false);

  const [isSearchMessages, setIsSearchMessages] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [keyDownTerm, setKeyDownTerm] = useState(null);
  /**
   * Módulo: Busca (mensagens no chat)
   * Busca mensagens dentro do chat atual com debounce simples.
   */
  const searchChatMessages = async () => {
    setSearchMessages([]);

    if (keyDownTerm) {

      if (keyDownTerm.key === "Backspace" && searchTerm === "") {
        setSearchMessages([]);
        setIsSearchMessages(false);
      } else {
        setIsSearchMessages(true);

        try {
          const response = await api.get(`/messages/chats/${currentChatId}/search?query=${searchTerm}`, { headers: { "Authorization": "Bearer " + user.token } });

          setSearchMessages(response.data);
        } catch (e) {
          
        } finally {
          setIsSearchMessages(false);
        }
      }
    }
  }

  useEffect(() => {
    const writeDelay = setTimeout(() => {
      searchChatMessages();
    }, 1500);
    return () => clearTimeout(writeDelay);
  }, [searchTerm]);

  /**
   * Módulo: Armazenamento local
   * Limpa caches de mensagens e controle de chats abertos.
   */
  const allStorage = () => {
    // delegate to hook utility
    clearLocalCache();
    return [];
  }



  /**
   * Módulo: Navegação por mensagem
   * Localiza e navega até uma mensagem específica dentro do chat.
   */
  const [messageIdChat, setMessageIdChat] = useState("");

  const findMessageWithinChat = async () => {
    if (!currentChatId || !messageIdChat) {
      openToast("error", "Ocorreu um erro", "Selecione uma conversa e informe a mensagem para buscar.");
      return;
    }

    try {
      const response = await api.get(`/messages/chats/search/data/${currentChatId}?id_message=${messageIdChat}`, { headers: { "Authorization": "Bearer " + user.token } });
      setMessages(response.data);
    } catch (error) {
      const message = error.response?.data?.message || "Não foi possível localizar a mensagem.";
      openToast("error", "Ocorreu um erro", message);
    }
  }

  // Navega para uma mensagem somente quando o usuário escolher uma mensagem.
  useEffect(() => {
    if (messageIdChat) {
      findMessageWithinChat();
    }
  }, [messageIdChat]);

  // Atualiza a lista somente quando o filtro de tags mudar.
  useEffect(() => {
    pegaChatsPorTags();
  }, [selectedTags]);

  

  /**
   * Módulo: Mensagens favoritas
   * Carrega e exibe mensagens favoritas do chat atual.
   */
  const [isFavorite, setIsFavorite] = useState(false);
  const [isFavoriteMessagesLoading, setIsFavoriteMessagesLoading] = useState(false);
  const [favoriteMessages, setFavoriteMessages] = useState([]);

  const fetchFavoriteMessages = async (chatId) => {
    setIsFavoriteMessagesLoading(true);
    setFavoriteMessages([]);

    try {

      const response = await api.get(`/messages/chats/starred/${chatId}`, { headers: { "Authorization": "Bearer " + user.token } });

      setFavoriteMessages(response.data);
    } catch (error) {
      
    } finally {
      setIsFavoriteMessagesLoading(false);
    }
  }

  const [buttonPopupSendMessage, setButtonPopupSendMessage] = useState(false);

  /**
   * Módulo: Fixar/Desafixar conversa
   * Fixa ou desfixa um chat no topo da lista.
   */
  const fixConversation = async (event, chatId) => {
    event?.preventDefault();
    setError("");

    try {
      const response = await api.post(
        `/chats/pin/${chatId}`,
        { chat_id: chatId },
        { headers: { "Authorization": "Bearer " + user.token } }
      );

      setButtonPopupSuccess(true);
      setSuccess(response.data.message);
    } catch (e) {
      setButtonPopupError(true);
      setError("Não foi possível realizar sua solicitação.");
      setButtonPopupError(true);
    }
  }

  const [barra, setBarra] = useState(0);

  /**
   * Módulo: Enviar mensagem (individual)
   * Envia texto, reply, imagens coladas e arquivos selecionados no chat atual.
   */
  const sendMessage = async () => {
    if (isSendingMessage) return;

    const templateStart = !currentReplyMessage && isWhatsappSessionClosed && message.trim() === "";

    if (message.trim() !== "" || templateStart) {
      if (currentReplyMessage && !templateStart) {
        setIsSendingMessage(true);
        let mensagem = message;
        setMessage("");

        // UI otimista: adiciona uma resposta temporária
        if (mensagem && mensagem.trim().length > 0) {
          const tempMsg = {
            id: `temp-${Date.now()}`,
            type: "text",
            body: mensagem,
            source: "output",
            createdAt: new Date().toISOString(),
            sent: true,
            sending: true,
            readAt: null,
            replyied: true,
            replyied_message: { type: "text", body: currentReplyMessage?.body }
          };
          setMessages(prev => [...prev, tempMsg]);
          setChatToBottom(prev => prev + 1);
          try {
            const cached = JSON.parse(localStorage.getItem(currentChatId)) || [];
            localStorage.setItem(currentChatId, JSON.stringify([...cached, tempMsg]));
          } catch {}
          updateChatPreview(currentChatId, getOutgoingPreview("text", mensagem));
        }

        try {
          const response = await api.post(`/messages/send/reply/` + currentReplyMessage.id, { chat_id: currentChatId, type: "text", body: mensagem }, { headers: { "Authorization": "Bearer " + user.token } });
          if (response.data?.error) {
            setMessage(mensagem);
            openToast("error", "Ocorreu um erro", response.data.message || "Falha ao enviar resposta.");
          }
        } catch (e) {
          setMessage(mensagem);
          openToast("error", "Ocorreu um erro", e.response?.data?.message || "Falha ao enviar resposta.");
        } finally {
          setBarra(0);
          await refreshChatsMeta();
          setIsSendingMessage(false);
          setIsEmojiModalOpen(false);
          setCurrentReplyMessage(null);
          // Concilia com o servidor para substituir a mensagem temporária
          await updateMessagesById(currentChatId, { force: true });
        }
      } else {
        setIsSendingMessage(true);
        let mensagem = message;
        setMessage("");

        // UI otimista: adiciona uma mensagem temporária de saída
        if ((mensagem && mensagem.trim().length > 0) || templateStart) {
          const previewText = templateStart ? "Template inicial" : mensagem;
          const tempMsg = {
            id: `temp-${Date.now()}`,
            type: "text",
            body: previewText,
            source: "output",
            createdAt: new Date().toISOString(),
            sent: true,
            sending: true,
            readAt: null
          };
          setMessages(prev => [...prev, tempMsg]);
          setChatToBottom(prev => prev + 1);
          try {
            const cached = JSON.parse(localStorage.getItem(currentChatId)) || [];
            localStorage.setItem(currentChatId, JSON.stringify([...cached, tempMsg]));
          } catch {}
          updateChatPreview(currentChatId, getOutgoingPreview("text", previewText));
        }

        try {
          const response = await api.post(`/messages/send`, {
            chat_id: currentChatId,
            type: "text",
            body: mensagem,
            template: templateStart
          }, { headers: { "Authorization": "Bearer " + user.token } });
          if (response.data?.error) {
            setMessage(mensagem);
            openToast("error", "Ocorreu um erro", response.data.message || "Falha ao enviar mensagem.");
          }
        } catch (e) {
          setMessage(mensagem);
          openToast("error", "Ocorreu um erro", e.response?.data?.message || "Falha ao enviar mensagem.");
        } finally {
          await refreshChatsMeta();
          setIsSendingMessage(false);
          setIsEmojiModalOpen(false);
          setBarra(0);
          // Concilia com o servidor para substituir a mensagem temporária
          await updateMessagesById(currentChatId, { force: true });
        }
      }
    }
    else if (pasteImages.length > 0) {
      setIsSendingMessage(true);
      setIsModalpasteImages(false);
      setIsEmojiModalOpen(false);
      const pendingImages = pasteImages.map((image, index) => ({
        id: `temp-${Date.now()}-image-${index}`,
        type: "image",
        body: pasteImagesSlider[index] || image,
        source: "output",
        createdAt: new Date().toISOString(),
        sent: true,
        sending: true,
        readAt: null,
      }));
      setMessages(prev => [...prev, ...pendingImages]);
      updateChatPreview(currentChatId, getOutgoingPreview("image"));
      setChatToBottom(prev => prev + 1);

      try {
        for (let i = 0; i < pasteImages.length; i++) {
          const res = await api.post(`/messages/send`, { chat_id: currentChatId, type: "image", format: ".jpg", body: pasteImages[i], blob: pasteImagesSlider[i], mime_type: "image/jpeg" }, { headers: { "Authorization": "Bearer " + user.token } });

          if (res.data.error) {
            openToast("error", "Ocorreu um erro", res.data.message);
          }
        }
        await updateMessagesById(currentChatId, { force: true });
        setpasteImages([]);
        setpasteImagesSlider([]);
        await refreshChatsMeta();
        setChatToBottom(prev => prev + 1);
      } catch (e) {
        openToast("error", "Ocorreu um erro", e.response?.data?.message || "Falha ao enviar imagem.");
      } finally {
        setIsSendingMessage(false);
      }
    }
    else if (selectedFiles.length > 0) {
      setIsSendingMessage(true);
      setIsModalSelectedFiles(false);
      setIsEmojiModalOpen(false);
      const pendingFiles = selectedFiles.map((file, index) => {
        const sliderFile = selectedFilesSlider[index] || {};
        const mimeType = normalizeWhatsappMediaMimeType(sliderFile.type || "application/octet-stream");
        const pendingType = mimeType.split("/")[0] === "application" ? "document" : mimeType.split("/")[0];

        return {
          id: `temp-${Date.now()}-file-${index}`,
          type: pendingType,
          body: sliderFile.path,
          source: "output",
          createdAt: new Date().toISOString(),
          sent: true,
          sending: true,
          readAt: null,
        };
      });
      setMessages(prev => [...prev, ...pendingFiles]);
      updateChatPreview(currentChatId, getOutgoingPreview(pendingFiles[pendingFiles.length - 1]?.type || "document"));
      setChatToBottom(prev => prev + 1);

      try {
        for (let i = 0; i < selectedFiles.length; i++) {
          let type = "";
          let format = "";
          const sliderFile = selectedFilesSlider[i] || {};
          let mimeType = normalizeWhatsappMediaMimeType(sliderFile.type || "application/octet-stream");
          if (mimeType.split("/")[0] == "application") {
            type = "document";
            format = ".pdf";
          } else {
            type = mimeType.split("/")[0];
            format = getWhatsappMediaFormat(mimeType);
          }

          const res = await api.post(`/messages/send`, { chat_id: currentChatId, type: type, body: selectedFiles[i], blob: sliderFile.path, format: format, mime_type: mimeType }, { headers: { "Authorization": "Bearer " + user.token } });

          if (res.data.error) {
            openToast("error", "Ocorreu um erro", res.data.message);
          }
        }
        await updateMessagesById(currentChatId, { force: true });
        setSelectedFiles([]);
        setSelectedFilesSlider([]);
        await refreshChatsMeta();
        setChatToBottom(prev => prev + 1);
      } catch (e) {
        openToast("error", "Ocorreu um erro", e.response?.data?.message || "Falha ao enviar arquivo.");
      } finally {
        setIsSendingMessage(false);
      }
    }
  }

  const [presets, setPresets] = useState([]);
  const [presetCommand, setPresetCommand] = useState("");
  const [isModalPresets, setIsModalPresets] = useState(false);

  /**
   * Módulo: Presets e Atalhos
   * Chama e filtra presets com atalho '/', insere no texto e controla a barra de sugestões.
   */
  const callPresets = async (command) => {
    let result = JSON.parse(localStorage.getItem("presets"));
    if (command !== "") {
      result = result.filter(preset => {
        return preset.command.toUpperCase().includes(command.toUpperCase());
      });
    }
    setPresets(result);
    setPresetCommand(command);
    setIsModalPresets(true);
    setIsEmojiModalOpen(true);
  }

  const handleTyping = async (event) => {

    if (event.key == "Enter" && event.ctrlKey == true) {
      setBarra(0);
      setIsModalPresets(false);
      if (chatInfo.sessao) setMessage(message + "\n");
    } else if (event.key == "Enter" && event.shiftKey == true && !chatInfo.sessao) {
      event.preventDefault();
    } else if (event.key == "Enter" && event.shiftKey == false && event.ctrlKey == false) {
      setChatToBottom(prev => prev + 1);
      setBarra(0);
      setIsModalPresets(false);
      event.preventDefault();
      sendMessage();
    } else if (event.key == " ") {
      setBarra(0);
      setIsModalPresets(false);
      setIsEmojiModalOpen(false);
    } else if (event.key == "/") {
      setBarra(1);
      callPresets("");
    } else if (barra == 1) {
      let messageSplit = event.target.value.split("/");
      event.key = (event.key == "Backspace") ? "" : event.key;
      let command = messageSplit[messageSplit.length - 1] + event.key;
      callPresets(command);
    }
  }

  const usePreset = (id) => {
    // pego o preset exato do cache
    let usedPreset = JSON.parse(localStorage.getItem("presets")).filter(item => {
      return parseInt(item.id) == id;
    });

    // subsitue o `comando (com a barra)` pelo body não formatado
    setMessage(message.replace("/" + presetCommand, usedPreset[0].body));

    // fecha o modal e etc
    setBarra(0);
    setIsModalPresets(false);
    setIsEmojiModalOpen(false);

    // focar no INPUT
    textArea.current.focus();
  }

  const [selectedChats, setSelectedChats] = useState([]);
  const [recordTrigger, setRecordTrigger] = useState(false);
  const [buttonPopupTags, setButtonPopupTags] = useState(false);
  const [buttonPopupNewContact, setButtonPopupNewContact] = useState(false);
  const [buttonPopupLigar, setButtonPopupLigar] = useState(false);



  async function pegaSocialMedia(id) {
    // Módulo: Redes sociais — Busca redes do cliente para seleção/atualização
    const options = { headers: { "Authorization": "Bearer " + user.token } };
    try {
      const response = await api.get("/users/socialmedia/" + id, options);

      setSocialMedias(response.data);
      setIdCustomer(id);

    } catch (error) {
      const message = error.response?.data?.message || "Não foi possível buscar as redes sociais do cliente.";
      setSuccess("");
      setError(message);
      openToast("error", "Ocorreu um erro", message);
    }
  }
  // Módulo: Informações do usuário — Atualiza dados no painel lateral
  const editInfoAdmin = async () => {
    const user_do_chat = await api.get(`/users/${info.id}`, { headers: { "Authorization": "Bearer " + user.token } });
    setInfo(user_do_chat.data);
  }

  const [buttonPopupSocialMedia, setButtonPopupSocialMedia] = useState(false);
  const [buttonPopupSocialMediaFilter, setButtonPopupSocialMediaFilter] = useState(false);
  const [buttonPopupChoose, setButtonPopupChoose] = useState(false);

  

  const [buttonPopupChatTags, setButtonPopupChatTags] = useState(false);


  const [filtroTag, setFiltroTag] = useState(false);

  // Master: filtrar por vendedor
  const [popupSeller, setPopupSeller] = useState(false);
  const [adminsLoading, setAdminsLoading] = useState(false);
  const [selectedSellerName, setSelectedSellerName] = useState("");

  const fetchAdminsList = async () => {
    try {
      setAdminsLoading(true);
      const response = await api.get("/admins/list?sales_only=1", tokenHeader);
      setAdmins(response.data);
    } catch (_) { }
    finally {
      setAdminsLoading(false);
    }
  };

  const openPopupSeller = () => {
    fetchAdminsList();
    setPopupSeller(true);
  };

  const toggleChatScope = () => {
    if (!canViewAllChats) return;

    const nextScope = chatScope === "all" ? "mine" : "all";

    adminId.current = 0;
    clearLocalCache();
    setSelectedTags([]);
    setFiltroTag(false);
    setSelectedSellerName("");
    setSearch("");
    setMasterGate(false);
    setIsNewChat(false);
    setIsChatChosen(false);
    setIsNewChatChosen(false);
    currentChatIdRef.current = "";
    setCurrentChatId("");
    setMessages([]);
    setInfo(undefined);
    currentChat.current = null;
    blockLongPooling.current = false;
    chatListRequest.current += 1;
    setChatScope(nextScope);
  };

  const filterBySeller = data => {
    adminId.current = data.seller;
    allStorage();
    setMasterGate(false);
    fetchAllChats();
    setIsNewChat(false);
    setPopupSeller(false);
    try {
      const found = (admins || []).find(a => String(a.id) === String(data.seller));
      setSelectedSellerName(found?.name || "");
    } catch (_) {
      setSelectedSellerName("");
    }
  };

  const loadAllChats = () => {
    adminId.current = 0;
    allStorage();
    setMasterGate(false);
    fetchAllChats();
    setSelectedSellerName("");
  };

  // Escolha inicial do master
  const handleMasterLoadAll = () => {
    adminId.current = 0;
    setMasterGate(false);
    allStorage();
    fetchAllChats();
  };

  const handleMasterFilter = () => {
    fetchAdminsList();
    setPopupSeller(true);
  };

  /**
   * Módulo: Filtro por tags
   * Limpa filtros e recarrega lista de chats.
   */
async function limparTags() {
    setSelectedTags([]);
    setFiltroTag(false);
    chatPage.current = 1;
    setHasMoreChats(true);
    await fetchAllChats({ tags: [] });
}

  /**
   * Busca chats filtrando por tags selecionadas.
   */
  async function pegaChatsPorTags() {
    if (!Array.isArray(selectedTags) || selectedTags.length <= 0) {
      return;
    }

    const requestScope = chatScopeRef.current;
    const requestId = ++chatListRequest.current;

    try {
      chatPage.current = 1;
      const data = await fetchByTags(selectedTags, {
        scope: isMasterMode ? "all" : "mine",
        adminId: adminId.current,
        page: 1,
        limit: CHAT_PAGE_SIZE,
      });
      if (requestId !== chatListRequest.current || requestScope !== chatScopeRef.current) return;

      const normalized = normalizeList(data);
      applyChatList(normalized);
      setHasMoreChats(normalized.length >= CHAT_PAGE_SIZE);
      setButtonPopupChatTags(false);
      setFiltroTag(true);
    } catch (error) {}
  }

  

  const [selectedChat, setSelectedChat] = useState([]);
  const [popupTransferConversation, setPopupTransferConversation] = useState(false);

  /**
   * Módulo: Transferir conversa
   * Abre modal e transfere a conversa para outro administrador.
   */
  const transferConversation = async (event, chat) => {
    event?.preventDefault();
    setError("");
    setSuccess("");

    fetchAdmins();

    setSelectedChat(chat);
    setPopupTransferConversation(true);
  }

  async function submitTransfer(data) {
    setError("");
    setSuccess("");

    const requestData = {
      admin_id: data.admin,
      user_id: selectedChat.user_id,
      socialMedia_id: selectedChat.socialMedia_id
    };

    try {
      const response = await api.post("/chats/transferir/conversa", requestData, { headers: { "Authorization": "Bearer " + user.token } });

      if (response.data.error) {
        setSuccess("");
        setError(response.data.message);
      } else {
        setError("");
        setSuccess(response.data.message);

        setPopupTransferConversation(false);
      }
    } catch (error) {
      const message = error.response?.data?.message || "Não foi possível transferir a conversa.";
      setSuccess("");
      setError(message);
      openToast("error", "Ocorreu um erro", message);
    }
  }

  const [admins, setAdmins] = useState([]);

  // Lista administradores para transferência
  async function fetchAdmins() {
    try {
      setInfoLoadingTransfer(true);
      const response = await api.get("/admins/list?sales_only=1", { headers: { "Authorization": "Bearer " + user.token } });

      setAdmins(response.data);
    } catch (error) {
      
    } finally {
      setInfoLoadingTransfer(false);
    }
  }

  /**
   * Módulo: Gravação de áudio
   * Controla botão de microfone (depende de sessão ativa no chat).
   */
  async function handleMic(e) {
    e.preventDefault();

    if (chatInfo.sessao) {
      setRecordTrigger(true);
    }
  }


  const hasChats = watch("chats");

  const showSelectedChats = e => {
    e.preventDefault();
  };

  const count = useRef(0);

  const onChangeCheckChat = (event, chat) => {
    let { checked } = event.target;

    checked ? count.current = count.current + 1 : count.current = count.current - 1;

    setList(list.map(oldList => {
      if (chat.id === oldList.id) {
        oldList.checked = checked;
      }

      return oldList;
    }));
  }

  const onChangeAllChats = event => {
    let { checked } = event.target;

    checked ? count.current =
      list.length : count.current = 0;

    setList(list.map(item => {
      item.checked = checked;

      return item;
    }));
  }

  let [chatsName, setChatsName] = useState("");

  const onSubmitChat = (event) => {
    event.preventDefault();

    setIsChatChosen(false);
    setIsChatSelected(true);
    currentChatIdRef.current = "";
    setCurrentChatId("");

    setChatsName("");
    setMessages([]);

    let selectedChats = list.filter(item => item.checked == true);

    setSelectedChats(selectedChats);

    selectedChats.forEach((chat, index) => {
      if (selectedChats.length - 1 === index) {
        setChatsName(oldState => oldState + chat.name);
      } else {
        setChatsName(oldState => oldState + chat.name + ", ");
      }
    });

    setMessages([{ id: 1, type: "warning", body: "Atenção, só é permitido o envio de uma mensagem. Após o envio – caso queira enviar mais mensagens – selecione os chats novamente." }]);
  }

  //ENVIAR MENSAGEM
  const sendMessageToMultiple = async () => {
    let ids = [];

    selectedChats.forEach(chat => ids.push(chat.chat_id));
    const headerConfig = { headers: { "Authorization": "Bearer " + user.token } };
    if (message !== "") {
      setMessages(previousData => [...previousData, { id: 1, type: "warning", body: "Por favor, aguarde o envio da mensagem." }]);

      try {
        ids.forEach(async id => {
          const textData = { chat_id: id, type: "text", body: message };

          const response = await api.post(`/messages/send/selection`, textData, headerConfig);

          if (response.data.error) {
            openToast("error", "Ocorreu um erro", response.data.message);
          } else {
            openToast("success", "Sucesso", response.data.message);
          }
        });

      } catch (err) {
        
      } finally {
        setIsChatChosen(false);
        setIsChatSelected(false);
        setChatsName("");
        setSelectedChats([]);
        setMessages([]);
        updateAllChats();
        setChatToBottom(prev => prev + 1);
      }
    } else if (pasteImages.length > 0) {
      setIsModalpasteImages(false);
      setIsEmojiModalOpen(false);

      for (let i = 0; i < selectedChats.length; i++) {
        for (let j = 0; j < pasteImages.length; j++) {
          await api.post(`/messages/send`, { chat_id: selectedChats[i].chat_id, type: "image", format: ".jpg", body: pasteImages[j], blob: pasteImagesSlider[j], mime_type: "image/jpeg" }, { headers: { "Authorization": "Bearer " + user.token } });
        }
      }

      await updateMessagesById(currentChatId, { force: true });
      setpasteImages([]);
      setpasteImagesSlider([]);
      setIsChatChosen(false);
      setIsChatSelected(false);
      setChatsName("");
      setSelectedChats([]);
      setMessages([]);
      updateAllChats();
    }
    else if (selectedFiles.length > 0) {
      setIsModalSelectedFiles(false);
      setIsEmojiModalOpen(false);

      for (let j = 0; j < selectedChats.length; j++) {
        if (selectedChats[j].socialMedia_id !== "")
          for (let i = 0; i < selectedFiles.length; i++) {
            let type = "";
            let format = "";
            let mimeType = normalizeWhatsappMediaMimeType(selectedFilesSlider[i].type);
            if (mimeType.split("/")[0] == "application") {
              type = "document";
              format = ".pdf";
            } else {
              type = mimeType.split("/")[0];
              format = getWhatsappMediaFormat(mimeType);
            }

            await api.post(`/messages/send`, { chat_id: selectedChats[j].chat_id, type: type, body: selectedFiles[i], blob: selectedFilesSlider[i].path, format: format, mime_type: mimeType }, { headers: { "Authorization": "Bearer " + user.token } });
          }
      }

      await updateMessagesById(currentChatId, { force: true });
      setIsChatChosen(false);
      setIsChatSelected(false);
      setChatsName("");
      setMessages([]);
      setSelectedChats([]);
      setSelectedFiles([]);
      updateAllChats();
    }
  }

  /**
   * Módulo: Arrastar e soltar (em massa)
   * Trata o arrastar/soltar de mídias no envio para múltiplos.
   */
  const onDrop = useCallback(async (acceptedFiles) => {
    const items = Array.from(acceptedFiles || []);

    const dataUrls = await Promise.all(
      items.map(
        f => new Promise(resolve => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(f);
        })
      )
    );
    const sliderUrls = items.map(f => URL.createObjectURL(f));

    setpasteImages(prev => [...prev, ...dataUrls]);
    setpasteImagesSlider(prev => [...prev, ...sliderUrls]);
    setIsModalpasteImages(true);
    setIsEmojiModalOpen(true);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    noClick: true,
    onDrop,
    accept: {
      "video/*": [],
      "image/*": [],
      "audio/*": [],
      "application/pdf": []
    }
  });

  /**
   * Enviar template — Mensagens pré-definidas.
   */
  const [tempTrigger, setTempTrigger] = useState(false);
  const [tempContractTrigger, setTempContractTrigger] = useState(false);

  const openTemplate = e => {
    e.preventDefault();
    setTempTrigger(true);
  }

  const openTemplateContract = e => {
    e.preventDefault();
    setTempContractTrigger(true);
  }

  const request = useHttp();

  // Navegação da barra lateral
  const favoritesPage = (e) => {
    e.preventDefault();
    setIsMessageSearch(false);
    setIsFavorite(true);
  };

  const searchPage = (e) => {
    e.preventDefault();
    setIsFavorite(false);
    setIsMessageSearch(true);
  };

  const informationPage = (e) => {
    e.preventDefault();
    setIsFavorite(false);
    setIsMessageSearch(false);
  }

  const inputFilesRef = useRef(null);

  const [unreadRes, setUnreadRes, unreadLoading, unreadError, unreadFetch] = useAxiosFunction();

  
  const markAsUnread = (e, id) => {
    e.preventDefault();

    unreadFetch({
      axiosInstance,
      method: "POST",
      url: "/chats/naolido/" + id,
      redirect_to: "",
      data: {}
    });

    if (!unreadLoading) {
      if (!unreadRes.error) {
        fetchAllChats();
      }
    }
  }

  /**
   *
   * Transferir conversas prospect
   */
  const [prospectRes, setProspectRes, prospectLoading, prospectError, prospectFetch] = useAxiosFunction();

  const [chatChosen, setChatChosen] = useState();
  const [popupProspect, setPopupProspect] = useState(false);

  const transferConversationProspect = async (event, chat) => {
    event?.preventDefault();
    fetchAdmins();
    setPopupProspect(true);
    setChatChosen(chat);
  }

  const transferProspect = data => {
    if (!chatChosen?.user_id || !chatChosen?.socialMedia_id) {
      openToast("error", "Ocorreu um erro", "Conversa não localizada para transferência.");
      return;
    }

    const selected = String(data.prospect || "");
    const isAdminTransfer = selected.startsWith("admin:");
    const adminId = selected.replace("admin:", "");

    prospectFetch({
      axiosInstance,
      method: "POST",
      url: isAdminTransfer ? "/chats/transferir/conversa" : "/chats/transferir/prospect/conversa",
      redirect_to: "",
      data: isAdminTransfer ? {
        "admin_id": adminId,
        "user_id": chatChosen.user_id,
        "socialMedia_id": chatChosen.socialMedia_id
      } : {
        "porte": data.prospect,
        "user_id": chatChosen.user_id,
        "socialMedia_id": chatChosen.socialMedia_id
      }
    });
  }

  useEffect(() => {
    if (prospectRes?.error === false) {
      setPopupProspect(false);
      resetProspect();
      updateAllChats();
    }
  }, [prospectRes, resetProspect]);

  const chooseChat = user => {
    pegaSocialMedia(user.id);

    if (available === false) {
      setButtonPopupSocialMediaFilter(true);
    } else {
      setButtonPopupChoose(true);
    }
  }

  /**
   * Via Consulti (desativado)
   */
  // const [consultiTrigger, setConsultiTrigger] = useState(false);

  const [email, setEmail] = useState(false);
  const [soares, setSoares] = useState(false);
  const [customerEmail, setCustomerEmail] = useState(false);
  // const openConsulti = e => {
  //   e.preventDefault();
  //   setConsultiTrigger(!consultiTrigger);
  // }


  const handleSendEmail = e => {
    e.preventDefault();
    setEmail(true);
  }

  const [triggerTransfer, setTriggerTransfer] = useState(false);
  const [modalConditional, setModalConditional] = useState(false);

  const mutationConditional = useMutation({
    mutationFn: () => request({ url: `/users/conditional/${currentChat.current.user_id}`, method: "post", data: {} }),
    onSuccess: response => {
      if (response.data?.error) {
        openToast("error", "Ocorreu um erro", response.data?.message);
      } else {
        openToast("success", "Sucesso", response.data?.message);
        setModalConditional(false);
      }
    }
  });

  const openModalConditional = () => {
    setModalConditional(true);
  };

  const conditionalCustomer = () => {
    mutationConditional.mutate();
  };

  return (
    <div className="chat-grid">  

      <ChatModals
        tempTrigger={tempTrigger}
        setTempTrigger={setTempTrigger}
        tempContractTrigger={tempContractTrigger}
        setTempContractTrigger={setTempContractTrigger}
        currentChatId={currentChatId}
        isChatSelected={isChatSelected}
        selectedChats={selectedChats}
        forwardMessages={forwardMessages}
        setforwardMessages={setforwardMessages}
        buttonPopupChatTags={buttonPopupChatTags}
        setButtonPopupChatTags={setButtonPopupChatTags}
        setSelectedTags={setSelectedTags}
        blockLongPooling={blockLongPooling}
        idCustomer={idCustomer}
        socialMedias={socialMedias}
        setMedia={setMedia}
        handleChatBack={handleChatBack}
        fetchMessagesById={fetchMessagesById}
        buttonPopupSocialMediaFilter={buttonPopupSocialMediaFilter}
        setButtonPopupSocialMediaFilter={setButtonPopupSocialMediaFilter}
        buttonPopupChoose={buttonPopupChoose}
        setButtonPopupChoose={setButtonPopupChoose}
        setAvailable={setAvailable}
        buttonPopupTags={buttonPopupTags}
        setButtonPopupTags={setButtonPopupTags}
        info={info}
        editInfoAdmin={editInfoAdmin}
        currentChat={currentChat}
        buttonPopupSocialMedia={buttonPopupSocialMedia}
        setButtonPopupSocialMedia={setButtonPopupSocialMedia}
        buttonPopupLigar={buttonPopupLigar}
        setButtonPopupLigar={setButtonPopupLigar}
        infoLoadingLigar={infoLoadingLigar}
        listPhones={listPhones}
        infoLoading={infoLoading}
        buttonPopupForward={buttonPopupForward}
        setButtonPopupForward={setButtonPopupForward}
        search={search}
        handleSearchByName={handleSearchByName}
        handleSearchUsersByName={handleSearchUsersByName}
        chats={chats}
        users={users}
        list={list}
        buttonPopupObs={buttonPopupObs}
        setButtonPopupObs={setButtonPopupObs}
        buttonPopupNewContact={buttonPopupNewContact}
        setButtonPopupNewContact={setButtonPopupNewContact}
        buttonPopupError={buttonPopupError}
        setButtonPopupError={setButtonPopupError}
        buttonPopupSuccess={buttonPopupSuccess}
        setButtonPopupSuccess={setButtonPopupSuccess}
        buttonPopupSendMessage={buttonPopupSendMessage}
        setButtonPopupSendMessage={setButtonPopupSendMessage}
        // Transferência de prospect
        popupProspect={popupProspect}
        setPopupProspect={setPopupProspect}
        registerProspect={registerProspect}
        handleSubmitProspect={handleSubmitProspect}
        prospectErrors={prospectErrors}
        prospectLoading={prospectLoading}
        transferProspect={transferProspect}
        // Modais extras
        modalConditional={modalConditional}
        setModalConditional={setModalConditional}
        conditionalCustomer={conditionalCustomer}
        mutationConditional={mutationConditional}
        soares={soares}
        setSoares={setSoares}
        customerEmail={customerEmail}
        setCustomerEmail={setCustomerEmail}
        // consultiTrigger={consultiTrigger}
        // setConsultiTrigger={setConsultiTrigger}
        triggerTransfer={triggerTransfer}
        setTriggerTransfer={setTriggerTransfer}
        email={email}
        setEmail={setEmail}
        popupTransferConversation={popupTransferConversation}
        setPopupTransferConversation={setPopupTransferConversation}
        infoLoadingTransfer={infoLoadingTransfer}
        admins={admins}
        register={register}
        handleSubmit={handleSubmit}
        submitTransfer={submitTransfer}
        user={user}
        error={error}
        success={success}
      />

      <div className="chat">
      <ChatList
        isNewChat={isNewChat}
        handleChatBack={handleChatBack}
        isModalOptionsOpen={isModalOptionsOpen}
        toggleOptionsMenu={toggleOptionsMenu}
        openNewContactPopup={openNewContactPopup}
        isChatsFetching={isChatsFetching}
        handleScroll={handleScroll}
        scrollRef={chatListScrollRef}
        openFilterByTags={openFilterByTags}
        clearTagsFilter={clearTagsFilter}
        isMasterMode={isMasterMode}
        canViewAllChats={canViewAllChats}
        chatScope={chatScope}
        toggleChatScope={toggleChatScope}
        openFilterBySeller={openPopupSeller}
        loadAllChats={loadAllChats}
        masterGate={masterGate}
        onMasterFilter={handleMasterFilter}
        onMasterLoadAll={handleMasterLoadAll}
        selectedSellerName={selectedSellerName}
        filtroTag={filtroTag}
        search={search}
        handleSearchByName={handleSearchByName}
        handleSearchUsersByName={handleSearchUsersByName}
        available={available}
          loadVacationContacts={loadVacationContacts}
          hasChats={hasChats}
          chats={chats}
          list={list}
          hasMoreChats={hasMoreChats}
          isLoadingMoreChats={isLoadingMoreChats}
          loadMoreChats={loadMoreChats}
          users={users}
          listUsers={listUsers}
          countRef={count}
          onChangeAllChats={onChangeAllChats}
          onChangeCheckChat={onChangeCheckChat}
          onSubmitChat={onSubmitChat}
          fetchMessagesById={fetchMessagesById}
          currentChatId={currentChatId}
          transferConversation={transferConversation}
          transferConversationProspect={transferConversationProspect}
          fixConversation={fixConversation}
          markAsUnread={markAsUnread}
          user={user}
          theme={theme}
          chooseChat={chooseChat}
        />

        {isMessagesFetching ? (
          <MessageInfo title="Carregando..." subtitle="Aguarde, suas mensagens aparecerão aqui" component={<Spinner color="primary" />} />
        ) : (
          isChatChosen ? (
            notAllowed ? (
              <MessageInfo title="Ocorreu um erro" subtitle={notAllowed} component={<Message />} />
            ) : (
              <div className={`${isEmojiModalOpen ? "chat-container-emoji" : "chat-container"}`}>

                <ChatHeader
                  name={info?.name && info?.name}
                  canTransfer={user.group_id == 8}
                  onTransferClick={() => setTriggerTransfer(true)}
                  showPhone={!isNewChatChosen}
                  onPhoneClick={() => { setButtonPopupLigar(true); pegaCliente(info.id) }}
                />

                <Messages
                  messages={messages}
                  setError={setError}
                  setSuccess={setSuccess}
                  replyMessage={replyMessage}
                  setButtonPopupError={setButtonPopupError}
                  setButtonPopupSuccess={setButtonPopupSuccess}
                  updateMessagesById={updateMessagesById}
                  setButtonPopupForward={setButtonPopupForward}
                  setforwardMessages={setforwardMessages}
                  chatToBottom={chatToBottom}
                />

                

                {isNewChatChosen && (
                  <div className="d-flex flex-column align-center mt-20">
                    <span className="mb-20">ESSE CHAT É UMA CONVERSA NOVA, CLIQUE AQUI PARA INICIAR</span>
                    <SmallButton title="Assumir Conversa" type="button" color="orange" loading={false} onClick={() => assumirConversa()} />
                  </div>
                )}
                <ChatComposer
                  isNewChatChosen={isNewChatChosen}
                  isEmojiModalOpen={isEmojiModalOpen}
                  setIsEmojiModalOpen={setIsEmojiModalOpen}
                  currentReplyMessage={currentReplyMessage}
                  closeReplyMessage={closeReplyMessage}
                  onEmojiClick={onEmojiClick}
                  isModalPresets={isModalPresets}
                  presets={presets}
                  usePreset={usePreset}
                  isModalpasteImages={isModalpasteImages}
                  pasteImagesSlider={pasteImagesSlider}
                  deletePasteImages={deletePasteImages}
                  isModalSelectedFiles={isModalSelectedFiles}
                  selectedFilesSlider={selectedFilesSlider}
                  deleteSelectedImages={deleteSelectedImages}
                  recordTrigger={recordTrigger}
                  setRecordTrigger={setRecordTrigger}
                  currentChatId={currentChatId}
                  updateAllChats={updateAllChats}
                  refreshChatsMeta={refreshChatsMeta}
                  updateMessagesById={updateMessagesById}
                  getRootProps={getRootProps}
                  getInputProps={getInputProps}
                  disableUnder24hMessage={isWhatsappSessionClosed}
                  message={message}
                  handleTyping={handleTyping}
                  setMessage={setMessage}
                  handlePasteArchives={handlePasteArchives}
                  handlePasteText={handlePasteText}
                  handleSendEmail={handleSendEmail}
                  // openConsulti={openConsulti}
                  openTemplate={openTemplate}
                  openTemplateContract={openTemplateContract}
                  sendMessage={sendMessage}
                  isSendingMessage={isSendingMessage}
                  textAreaRef={textArea}
                  chatInfo={chatInfo}
                  inputFileRef={inputFile}
                  handleFiles={handleFiles}
                  handleMic={handleMic}
                />
              </div>
            )
          ) : isChatSelected ? (
            <div className={`${isEmojiModalOpen ? "chat-container-emoji" : "chat-container"}`}>

              <ChatGroupHeader chatsName={chatsName} showSelectedChats={showSelectedChats} />

              <Messages
                onPaste={e => handleFiles(e)}
                messages={messages}
                setError={setError}
                setSuccess={setSuccess}
                setButtonPopupError={setButtonPopupError}
                setButtonPopupSuccess={setButtonPopupSuccess}
                updateMessagesById={updateMessagesById}
                setButtonPopupForward={setButtonPopupForward}
                setforwardMessages={setforwardMessages}
                chatToBottom={chatToBottom}
              />

              

              {isNewChatChosen && (
                <div className="d-flex flex-column align-center mt-20">
                  <span className="mb-20">ESSE CHAT É UMA CONVERSA NOVA, CLIQUE AQUI PARA INICIAR</span>
                  <SmallButton title="Assumir Conversa" type="button" color="orange" loading={false} onClick={() => assumirConversa()} />
                </div>
              )}
              <BulkComposer
                isEmojiModalOpen={isEmojiModalOpen}
                setIsEmojiModalOpen={setIsEmojiModalOpen}
                isModalpasteImages={isModalpasteImages}
                pasteImagesSlider={pasteImagesSlider}
                deletePasteImages={deletePasteImages}
                isModalSelectedFiles={isModalSelectedFiles}
                selectedFilesSlider={selectedFilesSlider}
                deleteSelectedImages={deleteSelectedImages}
                isModalPresets={isModalPresets}
                presets={presets}
                onEmojiClick={onEmojiClick}
                getRootProps={getRootProps}
                getInputProps={getInputProps}
                recordTrigger={recordTrigger}
                setRecordTrigger={setRecordTrigger}
                currentChatId={currentChatId}
                updateAllChats={updateAllChats}
                updateMessagesById={updateMessagesById}
                textAreaRef={textArea}
                message={message}
                handleTyping={handleTyping}
                setMessage={setMessage}
                handlePasteArchives={handlePasteArchives}
                handlePasteText={handlePasteText}
                handleSendEmail={handleSendEmail}
                openTemplateContract={openTemplateContract}
                openTemplate={openTemplate}
                inputFileRef={inputFile}
                handleFiles={handleFiles}
                sendMessageToMultiple={sendMessageToMultiple}
              />
            </div>
          ) : (
            <MessageInfo title="Suas mensagens aparecerão aqui" subtitle="Escolha uma conversa para iniciar" component={<Message />}>
              <div className="message-border"></div>
            </MessageInfo>
          )
        )}

      </div>

      <ChatSidebar
        isMessageSearch={isMessageSearch}
        isFavorite={isFavorite}
        favoritesPage={favoritesPage}
        searchPage={searchPage}
        informationPage={informationPage}
        info={info}
        infoLoading={infoLoading}
        setKeyDownTerm={setKeyDownTerm}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        searchMessages={searchMessages}
        isSearchMessages={isSearchMessages}
        favoriteMessages={favoriteMessages}
        isFavoriteMessagesLoading={isFavoriteMessagesLoading}
        setMessageIdChat={setMessageIdChat}
        openModalConditional={openModalConditional}
        inputFilesRef={inputFilesRef}
        setButtonPopupTags={setButtonPopupTags}
        setButtonPopupObs={setButtonPopupObs}
        setButtonPopupSocialMedia={setButtonPopupSocialMedia}
      />

      {isMasterMode && (
        <Popup title="Filtrar conversas por vendedor" trigger={popupSeller} setTrigger={setPopupSeller}>
          {adminsLoading ? (
            <LoadingCenter />
          ) : (
            <div className="chat__filter u-mb-1">
              <form onSubmit={handleSubmitSeller(filterBySeller)}>
                <ul className="filter__list">
                  {admins && admins.map((admin, index) => (
                    <label key={index} className="list__label">
                      <li className="list__item">
                        <input className="radio__btn" type="radio" name="seller" value={admin.id} {...registerSeller("seller")} />
                        <div className="list__item__image" style={{ backgroundImage: `url(${admin.profile_picture})` }} />
                        <p className="list__item__name">
                          {formatAdminTransferLabel(admin)}
                          <br />
                          Visto por Último: {admin.last_time_on}
                        </p>
                      </li>
                    </label>
                  ))}
                </ul>
                <div className="u-padding">
                  <button className="btn btn-primary" type="submit">Filtrar por vendedor</button>
                </div>
              </form>
            </div>
          )}
        </Popup>
      )}

    </div >
  );
}

export default Chat;
